import { create } from "zustand";

import type { ChiefNotice, Exercise } from "../../api/types";
import { t } from "../../i18n/i18n";
import { plural } from "../../plural";

/** Ordres transmis prévenus (décision d'Alexis du 13/10/2026 : « Oui, qu'ils soient prévenus »). Un dossier transmis
 *  dans la licence à un autre niveau que la compagnie (GTIA d'une brigade, brigade d'une division) ne suit pas l'ordre
 *  de son chef : quand celui-ci change, l'ordre rangé dans ses « Ordres reçus » est mis à jour tant qu'il n'a pas été
 *  reporté, et un avis le dit ; rien n'est reporté d'office. */

/** Dossier transmis dans la licence à un autre niveau que la compagnie (lien posé par le serveur) : ni décliné (il
 *  suit l'ordre de son chef), ni S/GTIA (son ordre lit celui du GTIA), ni remis par code (aucun lien). */
export function isTransmitted(exercise: Exercise | null | undefined): boolean {
  const extra = exercise?.parameters.extra;
  const chain = extra?.chain as { from?: unknown } | undefined;
  return Boolean(chain && typeof chain.from === "string" && chain.from && !extra?.declination && exercise?.parameters.level !== "COMPAGNIE");
}

/** « L'ordre de « 1BTEST » a changé depuis sa transmission : 3 paragraphes mis à jour. » */
export function chiefNoticeText(notice: Pick<ChiefNotice, "issuer" | "paragraphs" | "removed">): string {
  const paragraphs = plural(notice.paragraphs, t("paragraphe mis à jour"), t("paragraphes mis à jour"));
  // Unité retirée de l'ordre du chef (audit du 15/10/2026) : l'avis le dit en tête.
  if (notice.removed)
    return notice.issuer
      ? t("Votre unité ne figure plus dans l'ordre de « {chef} » : il a changé depuis sa transmission ({paragraphs}).", { chef: notice.issuer, paragraphs })
      : t("Votre unité ne figure plus dans l'ordre de l'échelon supérieur : il a changé depuis sa transmission ({paragraphs}).", { paragraphs });
  return notice.issuer
    ? t("L'ordre de « {chef} » a changé depuis sa transmission : {paragraphs}.", { chef: notice.issuer, paragraphs })
    : t("L'ordre de l'échelon supérieur a changé depuis sa transmission : {paragraphs}.", { paragraphs });
}

/** Ouverture demandée de l'ordre reçu qui porte l'avis : `view` (« Voir l'ordre reçu » : passages changés surlignés)
 *  ou `apply` (« Reporter les changements » : le report case par case est désigné). */
export interface ReceivedFocus {
  exerciseId: string;
  orderId: string;
  mode: "view" | "apply";
}

export const useReceivedFocus = create<{
  focus: ReceivedFocus | null;
  setFocus: (focus: ReceivedFocus | null) => void;
}>((set) => ({ focus: null, setFocus: (focus) => set({ focus }) }));

/** Lignes du texte changées depuis la dernière version vue (rendues par le serveur, sans blancs autour). */
export function changedSet(changed: readonly string[] | null | undefined): ReadonlySet<string> {
  return new Set((changed ?? []).map((line) => line.trim()).filter(Boolean));
}

/** Découpe un texte en lignes, chacune marquée si elle a changé. */
export function markLines(text: string, changed: ReadonlySet<string>): { line: string; changed: boolean }[] {
  return text.split("\n").map((line) => ({ line, changed: changed.size > 0 && changed.has(line.trim()) }));
}
